import { useEffect, useState } from 'react';
import { SanitizerService } from '../../../../services/sanitizer.service';
import Alert from '../../../Shared/Alert';
import Spinner from '../../../Shared/Spinner';
import { DomHelper } from '../../../../helpers/dom.helper';
import { LanguageService } from '../../../../services/language.service';
import type { IFindSearchResultItem } from '../../models/find-search-result-item.model';
import type { IFindSearchResults } from '../../models/find-search-results.model';
import EpiserverFindSuggestionApiModel from '../../models/episerver-find-suggestion.api-model';
import { EpiServerFindSearchResultsApiModel } from '../../models/episerver-find-search-results.api-model';
import { Status } from '../../../../hooks/fetch.hook';
import { useTexts } from '../../../../hooks/texts.hook';
import type { ISearchPageTexts } from '../..';

function processReceivedData(data: EpiServerFindSearchResultsApiModel): IFindSearchResults | undefined {
  return data ? EpiServerFindSearchResultsApiModel.mapToBusinessModel(data) : undefined;
}

function processEpiserverFindData(data: EpiserverFindSuggestionApiModel): string[] | undefined {
  return data ? EpiserverFindSuggestionApiModel.mapToBusinessModel(data) : undefined;
}

function SearchTabPanel({
  isActive,
  query = undefined,
  searchEndpoint,
  texts,
  onSearchCompleted,
  onSearchQueryChanged,
  numberOfResultsFunc,
}: {
  isActive: boolean;
  query: string | undefined;
  searchEndpoint: string;
  texts: ISearchPageTexts;
  onSearchCompleted: (numberOfUnifiedResults: number, numberOfFaqResults: number) => void;
  onSearchQueryChanged: (query: string) => void;
  numberOfResultsFunc: (data: IFindSearchResults) => number;
}) {
  const { t } = useTexts();
  const [currentPage, setCurrentPage] = useState(0);
  const [searchResults, setSearchResults] = useState<IFindSearchResultItem[]>([]);
  const [totalNumberOfSearchResults, setTotalNumberOfSearchResults] = useState(0);
  const [getSearchResultsStatus, setGetSearchResultsStatus] = useState(Status.notperformed);
  const [spellCheckData, setSpellCheckData] = useState<string | undefined>(undefined);
  const [didYouMeanData, setDidYouMeanData] = useState<string[]>([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(0);
    setSearchResults([]);
    setTotalNumberOfSearchResults(0);
    setGetSearchResultsStatus(Status.notperformed);
    setSpellCheckData(undefined);
    setDidYouMeanData([]);
  }, [query]);

  const dataUrl = query && (isActive || searchResults.length > 0) ? `/api/search/${searchEndpoint}?q=${query}&p=${currentPage}&language=${LanguageService.getCurrentLanguage()}` : undefined;

  useEffect(() => {
    async function fetchSpellCheckData() {
      try {
        const response = await fetch(`/find_v2/_spellcheck?size=1&query=${query}`);
        if (!response.ok) {
          setSpellCheckData(undefined);
          return;
        }
        const data = processEpiserverFindData(await response.json());
        setSpellCheckData(data && data.length > 0 ? data[0] : undefined);
      } catch {
        setSpellCheckData(undefined);
      }
    }

    async function fetchDidYouMeanData() {
      try {
        const response = await fetch(`/find_v2/_didyoumean?size=3&query=${query}`);
        if (!response.ok) {
          setDidYouMeanData([]);
          return;
        }
        const data = processEpiserverFindData(await response.json());
        setDidYouMeanData(data ? data : []);
      } catch {
        setDidYouMeanData([]);
      }
    }

    async function fetchData() {
      try {
        setSpellCheckData(undefined);
        setDidYouMeanData([]);
        setGetSearchResultsStatus(Status.loading);
        const response = await fetch(`/api/search/${searchEndpoint}?q=${query}&p=${currentPage}&language=${LanguageService.getCurrentLanguage()}`);
        if (!response.ok) {
          setGetSearchResultsStatus(Status.failed);
          return;
        }
        const data = processReceivedData(await response.json());
        setTotalNumberOfSearchResults(data ? numberOfResultsFunc(data) : 0);

        if (data) {
          onSearchCompleted?.(data.numberOfUnifiedResults, data.numberOfFaqResults);
        }

        setSearchResults(currentPage === 0 ? (data?.items ?? []) : (ps) => ps.concat(data?.items ?? []));

        if (data && numberOfResultsFunc(data) === 0) {
          await fetchSpellCheckData();
          await fetchDidYouMeanData();
        }

        setGetSearchResultsStatus(Status.success);
      } catch {
        setGetSearchResultsStatus(Status.failed);
      }
    }

    if (dataUrl) fetchData();
  }, [currentPage, dataUrl, numberOfResultsFunc, onSearchCompleted, query, searchEndpoint]);

  function handleOnSuggestionClicked(suggestion: string) {
    onSearchQueryChanged(suggestion);
  }

  function renderSearchResultItems() {
    return searchResults.map((item, i) => (
      <div key={i} className="border-bottom mb-3 print-no-break">
        <small className="result-section text-uppercase d-block mb-1">{item.section}</small>
        {item.url !== null ? (
          <a href={item.url} className="result-title d-inline-block">
            <h3>{item.title}</h3>
          </a>
        ) : (
          <h3>{item.title}</h3>
        )}
        <p dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(item.text) }}></p>
      </div>
    ));
  }

  function renderNoResults() {
    return (
      <div>
        <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.noUnifiedResultsContent) }}></div>
        {spellCheckData && (
          <p>
            {texts.emptySuggestion}
            <button className="btn btn-link px-1 py-1" onClick={() => handleOnSuggestionClicked(spellCheckData)}>
              {spellCheckData}
            </button>
            ?
          </p>
        )}
        {didYouMeanData?.length > 0 && (
          <div>
            <h3>{texts.noResultRelatedHeading}</h3>
            <ul className="m-0">
              {didYouMeanData.map((didYouMeanSuggestion) => (
                <li key={didYouMeanSuggestion}>
                  <button className="btn btn-link px-1 py-1" onClick={() => handleOnSuggestionClicked(didYouMeanSuggestion)}>
                    {didYouMeanSuggestion}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const showMoreButton = getSearchResultsStatus === Status.success && totalNumberOfSearchResults > searchResults.length;
  const showScrollTopButton = getSearchResultsStatus === Status.success && searchResults.length > 0;

  return (
    <div>
      <div>{renderSearchResultItems()}</div>
      {getSearchResultsStatus === Status.success && searchResults.length > 0 && (
        <div className="d-flex flex-column text-center">
          {showMoreButton && (
            <div>
              <button type="button" className="btn btn-tertiary btn-tertiary-position-center mb-1" onClick={() => setCurrentPage(currentPage + 1)}>
                {t('Hämta fler resultat', 'More results')}
              </button>
            </div>
          )}
          {searchResults.length > 0 && searchResults.length >= totalNumberOfSearchResults && <p>{t('Det finns inga fler matchande sökresultat.', 'There are no more matching search results.')}</p>}
          {showScrollTopButton && (
            <div>
              <button
                type="button"
                className="btn btn-tertiary btn-tertiary-position-center"
                onClick={() => {
                  const element = document.getElementById('search-page-autocomplete') as HTMLElement;
                  if (element !== null) {
                    element.scrollIntoView();
                    DomHelper.focusOnFirstFocusableElement(element);
                  }
                }}
              >
                {t('Till toppen', 'To the top')}
              </button>
            </div>
          )}
        </div>
      )}

      {getSearchResultsStatus === Status.success && searchResults.length === 0 && renderNoResults()}
      {getSearchResultsStatus === Status.loading && <Spinner delayInMs={100} />}
      <Alert show={getSearchResultsStatus === Status.failed} heading={t.generalError} closable={false} />
    </div>
  );
}

export default SearchTabPanel;

